import React, { useState, useEffect } from 'react';
import SafeIcon from '../common/SafeIcon';
import { FiZap, FiLock, FiAlertCircle, FiEye, FiEyeOff, FiCheckCircle } from 'react-icons/fi';
import { supabase } from '../supabase';

const ResetPasswordPage = ({ onDone }) => {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres.');
      return;
    }
    if (password !== confirm) {
      setError('Las contraseñas no coinciden.');
      return;
    }
    setLoading(true);
    const { error: updateError } = await supabase.auth.updateUser({ password });
    setLoading(false);
    if (updateError) {
      setError(updateError.message);
      return;
    }
    setSuccess(true);
    await supabase.auth.signOut();
  };

  const inputBase = {
    border: '1px solid #E2E8F0',
    color: '#0F172A',
    backgroundColor: '#F8FAFC',
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: '#F8FAFC' }}>
      <div className="w-full max-w-sm">
        {/* Brand */}
        <div className="flex items-center justify-center gap-3 mb-7">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{ background: 'linear-gradient(135deg,#2563EB,#38BDF8)', boxShadow: '0 4px 14px rgba(37,99,235,0.35)' }}>
            <SafeIcon icon={FiZap} className="text-white text-lg" />
          </div>
          <div className="leading-tight">
            <span className="text-xl font-black tracking-tight block" style={{ color: '#0F172A' }}>Fullpower</span>
            <span className="text-xs font-bold tracking-widest uppercase" style={{ color: '#2563EB' }}>LeadFlow</span>
          </div>
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl p-8" style={{ border: '1px solid #E2E8F0', boxShadow: '0 4px 24px rgba(15,23,42,0.07)' }}>
          {success ? (
            <div className="text-center space-y-4">
              <div className="flex justify-center">
                <div className="w-14 h-14 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: '#F0FDF4' }}>
                  <SafeIcon icon={FiCheckCircle} className="text-3xl" style={{ color: '#16A34A' }} />
                </div>
              </div>
              <h2 className="text-lg font-bold" style={{ color: '#0F172A' }}>¡Contraseña actualizada!</h2>
              <p className="text-sm" style={{ color: '#64748B' }}>
                Tu contraseña fue cambiada correctamente. Ya puedes iniciar sesión con tu nueva contraseña.
              </p>
              <button
                onClick={onDone}
                className="w-full py-2.5 rounded-lg text-white font-bold text-sm transition-all mt-2"
                style={{ backgroundColor: '#2563EB', boxShadow: '0 2px 8px rgba(37,99,235,0.3)' }}
                onMouseEnter={e => e.currentTarget.style.backgroundColor = '#1D4ED8'}
                onMouseLeave={e => e.currentTarget.style.backgroundColor = '#2563EB'}
              >
                Ir al inicio de sesión
              </button>
            </div>
          ) : (
            <>
              <h2 className="text-lg font-bold mb-1" style={{ color: '#0F172A' }}>Nueva contraseña</h2>
              <p className="text-sm mb-6" style={{ color: '#94A3B8' }}>Elige una contraseña segura para tu cuenta.</p>

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* New password */}
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider mb-1.5" style={{ color: '#64748B' }}>
                    Nueva contraseña
                  </label>
                  <div className="relative">
                    <SafeIcon icon={FiLock} className="absolute left-3 top-1/2 -translate-y-1/2 text-sm" style={{ color: '#94A3B8' }} />
                    <input
                      type={showPw ? 'text' : 'password'}
                      required
                      className="w-full pl-9 pr-10 py-2.5 rounded-lg text-sm outline-none transition-all"
                      style={inputBase}
                      placeholder="Mín. 6 caracteres"
                      value={password}
                      onChange={e => { setPassword(e.target.value); setError(''); }}
                      onFocus={e => { e.target.style.borderColor = '#2563EB'; e.target.style.backgroundColor = '#fff'; }}
                      onBlur={e => { e.target.style.borderColor = '#E2E8F0'; e.target.style.backgroundColor = '#F8FAFC'; }}
                    />
                    <button type="button" onClick={() => setShowPw(v => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 transition-colors"
                      style={{ color: '#94A3B8' }}
                      onMouseEnter={e => e.currentTarget.style.color = '#2563EB'}
                      onMouseLeave={e => e.currentTarget.style.color = '#94A3B8'}>
                      <SafeIcon icon={showPw ? FiEyeOff : FiEye} className="text-sm" />
                    </button>
                  </div>
                </div>

                {/* Confirm password */}
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider mb-1.5" style={{ color: '#64748B' }}>
                    Confirmar contraseña
                  </label>
                  <div className="relative">
                    <SafeIcon icon={FiLock} className="absolute left-3 top-1/2 -translate-y-1/2 text-sm" style={{ color: '#94A3B8' }} />
                    <input
                      type={showConfirm ? 'text' : 'password'}
                      required
                      className="w-full pl-9 pr-10 py-2.5 rounded-lg text-sm outline-none transition-all"
                      style={inputBase}
                      placeholder="Repite la contraseña"
                      value={confirm}
                      onChange={e => { setConfirm(e.target.value); setError(''); }}
                      onFocus={e => { e.target.style.borderColor = '#2563EB'; e.target.style.backgroundColor = '#fff'; }}
                      onBlur={e => { e.target.style.borderColor = '#E2E8F0'; e.target.style.backgroundColor = '#F8FAFC'; }}
                    />
                    <button type="button" onClick={() => setShowConfirm(v => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 transition-colors"
                      style={{ color: '#94A3B8' }}
                      onMouseEnter={e => e.currentTarget.style.color = '#2563EB'}
                      onMouseLeave={e => e.currentTarget.style.color = '#94A3B8'}>
                      <SafeIcon icon={showConfirm ? FiEyeOff : FiEye} className="text-sm" />
                    </button>
                  </div>
                </div>

                {error && (
                  <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm"
                    style={{ backgroundColor: '#FEF2F2', border: '1px solid #FECACA', color: '#EF4444' }}>
                    <SafeIcon icon={FiAlertCircle} className="text-base flex-shrink-0" />
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-2.5 rounded-lg text-white font-bold text-sm transition-all mt-1 disabled:opacity-70"
                  style={{ backgroundColor: '#2563EB', boxShadow: '0 2px 8px rgba(37,99,235,0.3)' }}
                  onMouseEnter={e => { if (!loading) e.currentTarget.style.backgroundColor = '#1D4ED8'; }}
                  onMouseLeave={e => e.currentTarget.style.backgroundColor = '#2563EB'}
                >
                  {loading ? 'Guardando…' : 'Guardar nueva contraseña'}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;